import { type AddressType, type IProject } from '@featit/shared';
import { findWorkAreaByRouteRef } from './workAreaRoute';

/**
 * I-0958: the work area the page on screen is about, which `WorkAreaScopeGate` puts the stores on
 * before the page renders: the one the address names, or, on a declared app route that reads the
 * selection, the selected one. `null` under All work areas, under a namespace, on the router's
 * catch-all, and on any address kind with no work area of its own.
 *
 * Pulled out of `Layout` (I-1024) so the one thing that went wrong there — treating *every* app
 * address as reading the selection — can be asserted without rendering it: `Layout` draws the catch-
 * all `/-/nope` with `boundary: true` (`addressRoutes.ts`'s `WORK_AREA_BOUNDARY_HANDLE`) before the
 * project list even answers; if this answered `selectedProjectId` for it too, the id went from unset
 * to the remembered work area the moment the list arrived, and `WorkAreaScopeGate` tore the page out
 * and put it back to enter a work area the not-found card was never drawn for.
 */
export function pageWorkAreaIdFor({
  here,
  allWorkAreas,
  boundaryMatched,
  selectedProjectId,
  projects,
}: {
  here: AddressType;
  /** Whether the reader is under All work areas — nothing on screen reads one selection then. */
  allWorkAreas: boolean;
  /** Whether the matched route is one `WorkAreaRouteBoundary` answers (`isWorkAreaBoundaryHandle`). */
  boundaryMatched: boolean;
  selectedProjectId: string | null | undefined;
  projects: readonly IProject[];
}): string | null | undefined {
  if (here.kind === 'work-area') return findWorkAreaByRouteRef(projects, here.reference)?.id;
  // A namespace's pages read its whole subtree (F-0383, §10.9), as All work areas reads every work
  // area: nothing there reads one selection. Answering the selection, the remembered work area
  // arrived with the list after a cold deep link's page had loaded the namespace's decisions, the
  // scope gate entered it, and the decisions store dropped the namespace's answer (gate 474a).
  if (here.kind === 'app' && here.route.startsWith('/namespaces/')) return null;
  if (here.kind === 'app' && !allWorkAreas && !boundaryMatched) return selectedProjectId;
  return null;
}
